import type { QualifyingStatusProduct } from '@iheartjane/data-access';
import { ProgressBar, repeatByCount, safeArrayLength } from '@iheartjane/menu';
import { Flex } from '@iheartjane/reefer';
import type { QualifyingStatusEnum } from '@iheartjane/types';

import { useKioskSpacing } from '../../../hooks';
import { ProductTile } from './ProductTile';
import type { ProductTileProps } from './ProductTile';
import { useSpecialDetailContext } from './SpecialDetail';

export interface SpecialDetailProgressProps {
  allowQualifiersToBeDiscounted: boolean;
  discountedProducts: QualifyingStatusProduct[];
  isGold?: boolean;
  onRemoveProduct: ProductTileProps['onRemoveProduct'];
  qualifiedProducts: QualifyingStatusProduct[];
  qualifiedProductsTotal?: number;
  qualifyBySpendAmount?: boolean;
  qualifyingStatus: QualifyingStatusEnum;
  qualifyingTargetAmount?: number;
  reachedMaxDiscountables: boolean;
  remainingQualifiers?: number;
  tagLabel: string;
}

export const SpecialDetailProgress = ({
  allowQualifiersToBeDiscounted,
  discountedProducts,
  isGold,
  onRemoveProduct,
  qualifyBySpendAmount,
  qualifiedProducts,
  qualifiedProductsTotal,
  qualifyingStatus,
  qualifyingTargetAmount,
  reachedMaxDiscountables,
  remainingQualifiers,
  tagLabel,
}: SpecialDetailProgressProps) => {
  const { scrolledFromTop } = useSpecialDetailContext();
  const { isIpad } = useKioskSpacing();
  const isApplied = qualifyingStatus === 'applied';
  const isQualified = qualifyingStatus === 'qualified';
  const isUnlocked = isQualified || isApplied;

  const qualifiedProductsToRender = repeatByCount(qualifiedProducts);
  const discountedProductsToRender = repeatByCount(discountedProducts);
  const remainingQualifierTiles = safeArrayLength(remainingQualifiers);

  return (
    <Flex flexDirection="column" gap={24}>
      <Flex
        alignItems="flex-start"
        justifyContent="center"
        flexWrap="wrap"
        gap={24}
        pt={isIpad || scrolledFromTop ? 16 : 24}
        data-testid="special-detail-progress"
      >
        {qualifiedProductsToRender.map((product, i) => (
          <ProductTile
            key={`${product.id}-${i}`}
            isDiscounted={allowQualifiersToBeDiscounted}
            isGold={isGold}
            isUnlocked={isUnlocked}
            onRemoveProduct={onRemoveProduct}
            product={product}
            tagLabel={tagLabel}
          />
        ))}

        {isUnlocked &&
          discountedProductsToRender.map((product, i) => (
            <ProductTile
              key={`${product.id}-${i}`}
              isDiscounted
              isGold={isGold}
              isUnlocked
              onRemoveProduct={onRemoveProduct}
              product={product}
              tagLabel={tagLabel}
            />
          ))}

        {Array.from({ length: remainingQualifierTiles }, (_, i) => (
          <ProductTile key={`qualifier-${i}`} />
        ))}

        {!allowQualifiersToBeDiscounted && !reachedMaxDiscountables && (
          <ProductTile
            isGold={isGold}
            isQualifiedForMore
            onRemoveProduct={onRemoveProduct}
            tagLabel={tagLabel}
          />
        )}
      </Flex>

      {qualifyBySpendAmount && (
        <ProgressBar
          qualifiedProductsTotal={qualifiedProductsTotal || 0}
          qualifyingTargetAmount={qualifyingTargetAmount || 0}
          unlocked={isUnlocked}
        />
      )}
    </Flex>
  );
};
